// Ganforge app shell — the instrument panel.
//
// Layout (from the desktop mocks): a 52px icon rail on the left, a 44px
// cell bar on top, the working surface in the middle, and a status strip
// along the bottom. Warm forge orange marks canonical truth; phosphor
// cyan marks the derived layer. The frame owns the atmosphere (grain +
// machining lines); pages own their panels.

import { onMount, Show, Suspense, type JSX } from "solid-js";
import { A, useLocation } from "@solidjs/router";
import "../../styles/forge.css";
import { AnvilMark } from "../public/Nav";
import { loadRepos, loadSettings } from "../../lib/ganforge";
import { connError, connLatency, connState, connTools, probeOnce } from "./connection";

function RailLink(props: {
  href: string;
  glyph: string;
  label: string;
  phos?: boolean;
  active: boolean;
}) {
  return (
    <A
      href={props.href}
      class="group relative w-9 h-9 grid place-items-center font-disp text-[15px] border transition-colors"
      classList={{
        "text-ink-faint border-transparent hover:text-ink": !props.active,
        "border-seam bg-iron-2": props.active,
        "text-forge-hi": props.active && !props.phos,
        "text-phos": props.active && props.phos,
      }}
      aria-label={props.label}
    >
      {props.glyph}
      <span
        class="absolute left-[46px] top-1/2 -translate-y-1/2 bg-iron-3 border border-seam-hi
          px-2 py-px text-[10px] tracking-[0.08em] whitespace-nowrap opacity-0
          group-hover:opacity-100 transition-opacity pointer-events-none z-50"
      >
        {props.label}
      </span>
    </A>
  );
}

export function ForgeShell(props: { children: JSX.Element }) {
  const loc = useLocation();
  onMount(probeOnce);

  const host = () => {
    const base = loadSettings().apiBase;
    if (!base) return null;
    try {
      return new URL(base).host;
    } catch {
      return base;
    }
  };
  const repoCount = () => loadRepos().length;
  const at = (prefix: string, exact = false) =>
    exact ? loc.pathname === prefix : loc.pathname.startsWith(prefix);

  return (
    <div class="forge-frame h-screen bg-iron-0 text-ink font-plex text-[13px] leading-normal grid overflow-hidden
        grid-cols-[64px_1fr] grid-rows-[44px_1fr_28px]
        [grid-template-areas:'rail_top'_'rail_main'_'rail_status']">
      {/* ---- left rail ---- */}
      <nav class="[grid-area:rail] bg-iron-1 border-r border-seam flex flex-col items-center py-2.5 gap-1.5">
        <A href="/forge" aria-label="Ganforge dashboard" class="mb-3.5" title="Ganforge">
          <AnvilMark class="h-12 w-12" />
        </A>
        <RailLink href="/forge/repos" glyph="⟠" label="REPOS" active={at("/forge/repos")} />
        <RailLink
          href="/forge/intelligence"
          glyph="◈"
          label="INTELLIGENCE"
          phos
          active={at("/forge/intelligence")}
        />
        <RailLink href="/forge/proposals" glyph="✉" label="PROPOSALS" active={at("/forge/proposals")} />
        <div class="flex-1" />
        <RailLink href="/forge/settings" glyph="⚙" label="SETTINGS" active={at("/forge/settings")} />
      </nav>

      {/* ---- top bar ---- */}
      <header class="[grid-area:top] bg-iron-1 border-b border-seam flex items-stretch overflow-x-auto">
        <div class="flex flex-col justify-center px-4 border-r border-seam">
          <A href="/forge" class="font-disp font-bold tracking-[0.06em] text-ink-dim whitespace-nowrap">
            GAN<span class="text-forge">FORGE</span>
          </A>
        </div>
        <div class="hidden sm:flex flex-col justify-center px-4 border-r border-seam min-w-0">
          <span class="font-disp text-[9px] tracking-[0.22em] uppercase text-ink-faint">Deployment</span>
          <span class="font-disp text-[13px] font-semibold truncate">
            <Show when={host()} fallback={<span class="text-ink-faint">not connected</span>}>
              {(h) => <span class="text-ink">{h()}</span>}
            </Show>
          </span>
        </div>
        <div class="hidden md:flex flex-col justify-center px-4 border-r border-seam">
          <span class="font-disp text-[9px] tracking-[0.22em] uppercase text-ink-faint">Repos</span>
          <span class="font-disp text-[13px] font-semibold">{repoCount()}</span>
        </div>
        <div class="flex-1" />
        <div class="hidden lg:flex items-center gap-4 px-4 border-l border-seam font-disp text-[10px] tracking-[0.14em] uppercase whitespace-nowrap">
          <span class="text-forge-hi">▎ canonical</span>
          <span class="text-phos">◈ derived</span>
        </div>
      </header>

      {/* ---- main ---- */}
      <main class="[grid-area:main] forge-main-bg overflow-y-auto overflow-x-hidden">
        {props.children}
      </main>

      {/* ---- status strip ---- */}
      <footer class="[grid-area:status] bg-iron-1 border-t border-seam flex items-center text-[10px] text-ink-dim overflow-x-auto whitespace-nowrap">
        <span class="h-full flex items-center gap-1.5 px-3 border-r border-seam">
          <span
            class="forge-dot"
            classList={{
              off: connState() === "unconfigured" || connState() === "no-repos",
              pulse: connState() === "checking",
              bad: connState() === "error",
            }}
          />
          {connState() === "ok"
            ? `index host ok · ${connTools().length} tools · ${connLatency() ?? "—"}ms`
            : connState() === "checking"
              ? "probing index host…"
              : connState() === "error"
                ? connError()
                : connState() === "no-repos"
                  ? "connected host, no repos tracked yet"
                  : "no deployment configured"}
        </span>
        <span class="h-full hidden sm:flex items-center px-3 border-r border-seam">
          answers stamped good-until · AI proposes, never writes history
        </span>
        <span class="h-full flex items-center px-3 ml-auto text-ink-faint">ganforge web</span>
      </footer>
    </div>
  );
}

export function ForgeLoading() {
  return (
    <div class="flex items-center justify-center py-16 gap-2 text-ink-faint font-plex text-[12px]">
      <span class="forge-dot phos pulse" />
      reading the panel…
    </div>
  );
}

/** Route wrapper: shell + Suspense WITH an explicit fallback (house
 *  protocol — every lazy route is wrapped exactly like this). */
export function withForge(Page: () => JSX.Element) {
  return () => (
    <ForgeShell>
      <Suspense fallback={<ForgeLoading />}>
        <Page />
      </Suspense>
    </ForgeShell>
  );
}
